<template>
  <br />
  <div style="padding:5px" class="row">
    <div class="col-lg-4 col-md-6 col-sm-12 mb-3">
      <label for="search" class="form-label mt-2 me-2">Keyword</label>
      <input id="search" class="form-control" placeholder="Subject" v-model="keyword"
        @input="searchKeyword" />
    </div>
    <div class="col-lg-4 col-md-6 col-sm-12 mb-3">
        <label for="locale" class="form-label">Locale</label>
        <select id="locale" v-model="locale" class="form-control" required @change="localeFilter(locale)">
            <option value="">All</option>
            <option value="en">EN</option>
            <option value="lt">LT</option>
            <option value="lv">LV</option>
            <option value="ee">EE</option>
            <option value="it">IT</option>
            <option value="es">ES</option>
            <option value="ro">RO</option>
            <option value="ua">UA</option>
        </select>
    </div>
    <div class="col-lg-4 col-md-6 col-sm-12 mb-3">
      <label for="per-page" class="form-label me-1 mt-2">Per page</label>
      <select id="per-page" class="form-control" v-model="perPage" @change="perPageFilter(perPage)">
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
      </select>
    </div>
    <div class="d-flex mb-3">
      <div >
        <router-link
            :to="{ name: 'CreateOfficialMessage' }"
            class="btn btn-success form-control"
            aria-label="Create new message"
        >
          <span>Create message</span>
        </router-link>
      </div>
    </div>

    <div class="alert alert-success" role="alert" v-if="success">
      {{ success }}
    </div>
    <div class="alert alert-danger" role="alert" v-if="error">
      {{ error }}
    </div>

    <table class="table">
      <thead>
        <tr>
          <th scope="col"># ID</th>
          <th scope="col">Subject</th>
          <th scope="col">Message</th>
          <th scope="col">Locale</th>
          <th scope="col">Created at</th>
          <th scope="col">Edit at</th>
          <th scope="col">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="loading">
          <td colspan="7" class="text-center"><font-awesome-icon icon="fa-solid fa-sync fa-10x" spin /></td>
        </tr>
        <tr v-else-if="messages.length === 0">
          <td colspan="7" class="text-center">No messages found</td>
        </tr>
        <template v-else>
          <tr v-for="message in messages" :key="message.id">
            <th scope="row">{{ message.id }}</th>
            <td>
              <p>{{ message.subject }}</p>
            </td>
            <td>
              {{ truncateString(message.message, 30) }}
            </td>
            <td>{{ message.conversation_locale }}</td>
            <td>{{ message.created_at }}</td>
            <td>{{ message.edited_at}}</td>
            <td>
              <button type="button" class="btn btn-primary"
                @click="this.$router.push({ name: 'EditOfficialMessage', params: { id: message.id } })">
                Edit
              </button>
              <button type="button" class="btn btn-danger"
                @click="deleteMessage(message.id)">
                Delete
              </button>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <div class="d-flex justify-content-between" v-if="totalRecords >= 1">
      <div>
        <div class="p-2">
          <p>Total messages: {{ totalRecords }}</p>
        </div>
      </div>
      <div>
        <div class="p-2">
          <nav v-if="totalPages >= 1" aria-label="Page navigation example">
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage - 1 < 1 }">
                <a @click="paginate(currentPage - 1)" class="page-link">Previous</a>
              </li>
              <li class="page-item" :class="{ active: 1 === currentPage }">
                <a @click="paginate(1)" class="page-link" :class="{ 'not-clickable': currentPage === 1 }">1</a>
              </li>
              <li class="page-item" v-if="currentPage - 2 > 1">
                <a href="#" class="page-link">...</a>
              </li>
              <li class="page-item" v-if="currentPage - 2 > 1">
                <a @click="paginate(currentPage - 2)" class="page-link">{{ currentPage - 2 }}</a>
              </li>
              <li class="page-item" v-if="currentPage - 1 > 1">
                <a @click="paginate(currentPage - 1)" class="page-link">{{ currentPage - 1 }}</a>
              </li>
              <li class="page-item" v-if="currentPage !== 1" :class="{ active: currentPage }">
                <a @click="paginate(currentPage)" class="page-link" :class="{ 'not-clickable': currentPage }">{{
                  currentPage }}</a>
              </li>
              <li class="page-item" v-if="currentPage + 1 <= totalPages">
                <a @click="paginate(currentPage + 1)" class="page-link">{{ currentPage + 1 }}</a>
              </li>
              <li class="page-item" v-if="currentPage + 2 <= totalPages">
                <a @click="paginate(currentPage + 2)" class="page-link">{{ currentPage + 2 }}</a>
              </li>
              <li class="page-item" v-if="currentPage + 2 < totalPages">
                <a href="#" class="page-link">...</a>
              </li>
              <li class="page-item" v-if="currentPage !== totalPages && currentPage + 2 < totalPages"
                :class="{ active: totalPages === currentPage }">
                <a @click="paginate(totalPages)" class="page-link"
                  :class="{ 'not-clickable': totalPages === currentPage }">{{ totalPages }}</a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage + 1 > totalPages }">
                <a @click="paginate(currentPage + 1)" class="page-link">Next</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import axios from 'axios';
import _debounce from 'lodash/debounce';
import Swal from "sweetalert2";

export default {
  data() {
    return {
      loading: true,
      messages: [],
      keyword: '',
      locale: '',
      currentPage: 1,
      totalPages: 0,
      totalRecords: 0,
      perPage: 10,
      success: '',
      error: ''
    }
  },

  mounted() {
    this.getMessages('', 1, this.perPage, null);
  },

  methods: {
    debouncedGetMessages: _debounce(function (keyword, page, perPage, locale) {
        this.loading = true;
        this.error = '';
        axios.get(`/admin/conversations/messages/official`, {
          params: {
              keyword: keyword,
              page: page,
              per_page: perPage,
              conversation_locale: locale
          }
        })
      .then(response => {
        this.messages = response.data.data;
        this.totalPages = response.data.last_page;
        this.totalRecords = response.data.total;
        this.loading = false;
      })
        .catch(error => {
          this.loading = false;
        });
    }, 500),
    getMessages(keyword, page, perPage, locale) {
      this.loading = true;
      this.debouncedGetMessages(keyword, page, perPage, locale);
    },
    searchKeyword() {
      this.currentPage = 1;
      this.getMessages(this.keyword, this.currentPage, this.perPage, this.locale);
    },
    perPageFilter(perPage) {
      this.perPage = perPage;
      this.currentPage = 1;
      this.getMessages(this.keyword, this.currentPage, this.perPage, this.locale);
    },
    paginate(page) {
      this.currentPage = page;
      this.getMessages(this.keyword, this.currentPage, this.perPage, this.locale);
    },
    localeFilter(locale) {
      this.locale = locale;
      this.currentPage = 1;
      this.getMessages(this.keyword, this.currentPage, this.perPage, this.locale);
    },

    truncateString(str, maxLength) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(str, 'text/html');
      const newSrt = doc.body.textContent || "";
      if (newSrt.length > maxLength) {
          return newSrt.slice(0, maxLength - 3) + '...';
      }
      return newSrt;
    },
    deleteMessage(messageId) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'You will not be able to recover this message!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'No, keep it'
      }).then((result) => {
        if (result.isConfirmed) {
          axios.delete(`/admin/conversations/messages/official/${messageId}`)
            .then(response => {
              this.success = 'Message deleted successfully.';
              this.getMessages(this.keyword, this.currentPage, this.perPage, this.locale);
            })
            .catch(error => {
              this.error = 'Failed to delete the message.';
            });
        }
      });
    }
  }
}
</script>
